import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { reactular } from '@vendhq/reactular'
import { Card, Scrollable } from '@lightspeed/design-system-react'
import { ApiCustomer, ApiRetailerPaymentType } from '@vend/types'
import classNames from 'classnames'
import { vn } from 'vend-number'
import { isReturn } from '../../../functions/sale'
import {
  hasActiveLspCashDiscountPercentage,
  shouldApplyCashDiscountingServiceFee,
} from '../../../functions/payments'
import {
  FEATURE_LSPAY_CASH_DISCOUNTING,
  FEATURE_LSPAY_CNP_PAYMENTS,
  FEATURE_LSPAY_CP_CNP_SPLIT_ENTRY,
} from '../../../constants/wr-features'
import { getFeatureFromCache } from '../../../util/feature-cache'
import { useRegisterManager, useSaleManager } from '../../../ng-compat/hooks'
import { useAppSelector } from '../../../state/hooks'
import {
  selectOutstandingBalance,
  selectSaleLocalId,
  selectCustomer,
  selectLspCashDiscountPercentage,
  selectSaleTotals,
  selectPayAccountBalance,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { useFormatCurrencyValue } from '../summary-panel/useFormatCurrencyValue'
import { PayLinkLimitModal } from '../../payments/pay-link-limit-modal/PayLinkLimitModal'
import { SaleModals } from '../SaleModals'
import { CashDiscountingBanner } from './CashDiscountingBanner'
import { CashDiscountingMigrationBanner } from './CashDiscountingMigrationBanner'
import { LspCashDiscountFullPaymentBanner } from './LspCashDiscountFullPaymentBanner'
import { LspCashDiscountingBanner } from './LspCashDiscountingBanner'
import { PaymentOptions } from './PaymentOptions'
import { useFetchPaymentTypes } from './hooks/useFetchPaymentTypes'
import { useLspCashDiscountBlockedSale } from './hooks/useLspCashDiscountBlockedSale'
import { useDefaultPayAmount } from './hooks/useDefaultPayAmount'
import { LSPayButton } from './LSPayButton'
import { LSPaySplitButton } from './LSPaySplitButton'
import { AmountTendered } from './AmountTendered/AmountTendered'
import { usePaymentInit } from './hooks/usePaymentInit'
import { CustomerSection } from './CustomerSection'
import { useShowSaveSaleButton } from './hooks/useShowSaveSaleButton'
import { SaveSale } from './SaveSale'
import { CustomerPaymentOptions } from './CustomerPaymentOptions'
import { CustomerPaymentTypeMessage } from './CustomerPaymentTypeMessage/CustomerPaymentTypeMessage'
import { usePreventPageReload } from './hooks/usePreventPageReload'
import { useHandlePaymentType } from './hooks/useHandlePayment'
import { PaymentModal } from './PaymentModal/PaymentModal'
import { useLaybyOnAccountSale } from './hooks/useLaybyOnAccountSale'
import {
  RecoveryPaymentOptions,
  useTransactionRecovery,
} from './hooks/useTransactionRecovery'
import { useLSPayQuickPay } from './hooks/useLSPayQuickPay'
import { PayPalRefundButton } from './PayPalRefundButton'
import { useEcomPaymentTypes } from './hooks/useEcomPaymentTypes'
import { LSPayFlow } from './PaymentModal/types'
import { PaymentRequestButton } from './PaymentRequest/PaymentRequestButton'
import { PaymentRequestSummaryCard } from './PaymentRequest/PaymentRequestSummaryCard'
import { usePaymentRequests } from './PaymentRequest/hooks/usePaymentRequests'
import { useAutoCancelPaymentRequests } from './PaymentRequest/hooks/useAutoCancelPaymentRequests'
import { useHasCustomerSavedPaymentMethods } from './LSPayPaymentMethodOnFile/useHasCustomerSavedPaymentMethods'
import {
  PaymentRecalculationLockProvider,
  usePaymentRecalculationLock,
} from './PaymentRecalculationLockContext'
import { isLspCashDiscountBlocked } from './isPartialAmountTendered'
import { isAbpPaymentUiFlowActive } from './isAbpPaymentUiFlowActive'

export type PaymentData = {
  recoveryPaymentOptions?: RecoveryPaymentOptions
  // Optional override to route specific LSPay sub-flows while keeping the same paymentType.
  lspayFlow?: LSPayFlow
  // For PMOF: the selected saved payment method id to charge
  paymentMethodOnFileId?: string
}

const PaymentPanelContent = () => {
  const [paymentProcessing, setPaymentProcessing] = useState(false)
  const saleManager = useSaleManager()
  const registerManager = useRegisterManager()

  const formatCurrencyValue = useFormatCurrencyValue()

  const cashDiscountingOutstandingBalance = saleManager.getCashDiscountedOutstandingBalance()
  const {
    filteredPaymentTypes,
    systemPaymentTypes,
    fetchPaymentTypes,
  } = useFetchPaymentTypes()
  const eComPaymentTypes = useEcomPaymentTypes()
  const { allowPageReload, preventPageReload } = usePreventPageReload()
  const localId = useAppSelector(selectSaleLocalId)
  const outstandingBalance = useAppSelector(selectOutstandingBalance)
  const lspCashDiscountPercentage = useAppSelector(
    selectLspCashDiscountPercentage
  )
  const saleTotals = useAppSelector(selectSaleTotals)
  const lspCashDiscountBlockedSale = useLspCashDiscountBlockedSale()
  const lspCashDiscountingEnabled = getFeatureFromCache(
    FEATURE_LSPAY_CASH_DISCOUNTING
  )
  const showCashDiscountingBanner = shouldApplyCashDiscountingServiceFee(
    registerManager.hasCashDiscounting(),
    {
      totals: saleTotals,
      lspCashDiscountPercentage,
    }
  )

  const showCashDiscountingMigrationBanner =
    lspCashDiscountingEnabled &&
    registerManager.hasCashDiscounting() &&
    !hasActiveLspCashDiscountPercentage({ lspCashDiscountPercentage })
  const customer = useAppSelector(selectCustomer)
  const payAccountBalance = useAppSelector(selectPayAccountBalance)
  const defaultPayAmount = useDefaultPayAmount()
  const [amountTendered, setAmountTendered] = useState(defaultPayAmount)

  const showSplitEntry =
    getFeatureFromCache(FEATURE_LSPAY_CNP_PAYMENTS) &&
    getFeatureFromCache(FEATURE_LSPAY_CP_CNP_SPLIT_ENTRY)

  const [showSaveSaleButton] = useShowSaveSaleButton()

  /**
   * Automatically update amountTendered when the outstanding balance changes.
   * In the account balance flow the Pay input drives payAccountBalance.amount, which feeds back
   * into selectOutstandingBalance. Syncing here would overwrite what the merchant is typing.
   */
  useEffect(() => {
    if (isAbpPaymentUiFlowActive(payAccountBalance)) return
    setAmountTendered(defaultPayAmount)
  }, [
    defaultPayAmount,
    outstandingBalance,
    lspCashDiscountBlockedSale.payments,
    payAccountBalance,
  ])

  // register & deregister init events once when component mount / unmount
  usePaymentInit({ fetchPaymentTypes })

  const [hasPaymentAmountError, setHasPaymentAmountError] = useState(false)

  const {
    handlePayment,
    handleCompletePayment,
    selectedPaymentType,
    clearSelectedPaymentType,
  } = useHandlePaymentType(
    amountTendered,
    paymentProcessing,
    hasPaymentAmountError,
    setPaymentProcessing,
    allowPageReload
  )

  const pay = (
    paymentType: ApiRetailerPaymentType,
    paymentData?: PaymentData
  ) => {
    preventPageReload()
    handlePayment(paymentType, paymentData)
  }

  //  If we have a payment to recover, we need to handle that first
  useTransactionRecovery({
    lspayPaymentType: systemPaymentTypes.lspayPaymentType,
    pay,
  })

  //  Handle the case for lspay quick pay
  useLSPayQuickPay({
    lspayPaymentType: systemPaymentTypes.lspayPaymentType,
    pay,
  })

  const {
    lockPaymentActionsUntilRecalculationCompletes,
  } = usePaymentRecalculationLock()

  const handleCustomerSelect = (customer?: ApiCustomer) => {
    if (!customer) {
      return
    }
    lockPaymentActionsUntilRecalculationCompletes()
    return saleManager.addCustomer(customer)
  }

  const {
    laybyOnAccountSale,
    isPayLinkModalOpen,
    handlePayLinkLimitModalClose,
  } = useLaybyOnAccountSale(
    localId,
    amountTendered,
    paymentProcessing,
    setPaymentProcessing
  )

  const {
    paymentRequests,
    refetch: refetchPaymentRequests,
  } = usePaymentRequests()

  const {
    hasSavedMethods: customerHasSavedCards,
    isResolved: savedCardsResolved,
  } = useHasCustomerSavedPaymentMethods()

  const paymentRequestButtonProps = {
    amountTendered,
    paymentRequests,
    onRefetchPaymentRequests: refetchPaymentRequests,
    hasPaymentAmountError,
  }

  useAutoCancelPaymentRequests({
    paymentRequests,
    outstandingBalance,
    saleLocalId: localId,
    customer,
    refetchPaymentRequests,
  })

  const isLspCashDiscountingBlocked = isLspCashDiscountBlocked(
    amountTendered,
    lspCashDiscountPercentage,
    lspCashDiscountBlockedSale
  )

  return (
    <>
      {createPortal(<SaleModals />, document.body)}
      <Card className="payment-panel--action">
        <Scrollable monobrow={true} goatee={true}>
          <div
            className={classNames(
              'vd-flex vd-flex--column payment-panel-content--action',
              {
                'pro-payments-ready': !paymentProcessing,
              }
            )}
          >
            {showCashDiscountingMigrationBanner ? (
              <CashDiscountingMigrationBanner />
            ) : showCashDiscountingBanner &&
              cashDiscountingOutstandingBalance ? (
              <CashDiscountingBanner
                balance={formatCurrencyValue(cashDiscountingOutstandingBalance)}
              />
            ) : null}
            {!isReturn(lspCashDiscountBlockedSale) ? (
              <>
                {lspCashDiscountPercentage > 0 &&
                !isLspCashDiscountingBlocked ? (
                  <LspCashDiscountingBanner rate={lspCashDiscountPercentage} />
                ) : null}
                {lspCashDiscountPercentage > 0 &&
                isLspCashDiscountingBlocked ? (
                  <LspCashDiscountFullPaymentBanner />
                ) : null}
              </>
            ) : null}
            <AmountTendered
              amountTendered={amountTendered}
              onChange={setAmountTendered}
              setHasError={setHasPaymentAmountError}
              reserveCashDiscountBannerSpace={
                showCashDiscountingBanner &&
                !showCashDiscountingMigrationBanner &&
                Boolean(cashDiscountingOutstandingBalance)
              }
            />
            <PayPalRefundButton
              paymentType={eComPaymentTypes.paypalEcomPaymentType}
              pay={pay}
            />
            {showSplitEntry ? (
              <LSPaySplitButton
                paymentType={systemPaymentTypes.lspayPaymentType}
                pay={pay}
                disabled={hasPaymentAmountError}
                amountTendered={amountTendered}
              />
            ) : (
              <LSPayButton
                paymentType={systemPaymentTypes.lspayPaymentType}
                pay={pay}
                disabled={hasPaymentAmountError}
                amountTendered={amountTendered}
                paymentModalOpen={!!selectedPaymentType}
              />
            )}
            <PaymentOptions
              pay={pay}
              amountTendered={amountTendered}
              hasValidTenderedAmount={!hasPaymentAmountError}
              filteredPaymentTypes={filteredPaymentTypes}
              systemPaymentTypes={systemPaymentTypes}
            />
            <CustomerSection
              onSelect={handleCustomerSelect}
              canAddAccountBalancePayment={true}
            />
            <PaymentRequestSummaryCard
              paymentRequests={paymentRequests}
              onRefetch={refetchPaymentRequests}
              outstandingBalance={outstandingBalance}
              amountTendered={amountTendered}
            />
            {savedCardsResolved && !customerHasSavedCards && (
              <PaymentRequestButton {...paymentRequestButtonProps} />
            )}
            <CustomerPaymentOptions
              systemPaymentTypes={systemPaymentTypes}
              pay={pay}
              laybyOnAccountSale={laybyOnAccountSale}
              amountTendered={amountTendered}
              hasPaymentAmountError={hasPaymentAmountError}
              paymentRequestButton={
                savedCardsResolved && customerHasSavedCards ? (
                  <PaymentRequestButton
                    {...paymentRequestButtonProps}
                    fullWidth={false}
                  />
                ) : undefined
              }
            />

            <CustomerPaymentTypeMessage amountTendered={amountTendered} />
          </div>
        </Scrollable>
        {showSaveSaleButton && (
          <div className="sale-action-bar save-sale">
            <SaveSale />
          </div>
        )}
      </Card>
      {selectedPaymentType && (
        <PaymentModal
          tenderAmount={amountTendered}
          paymentOptions={selectedPaymentType.paymentOptions}
          onClose={clearSelectedPaymentType}
          onSuccess={handleCompletePayment}
        />
      )}
      {isPayLinkModalOpen && (
        <PayLinkLimitModal
          amount={vn(amountTendered).toNumber()}
          close={handlePayLinkLimitModalClose}
        />
      )}
    </>
  )
}

export const PaymentPanel = () => (
  <PaymentRecalculationLockProvider>
    <PaymentPanelContent />
  </PaymentRecalculationLockProvider>
)

export const ReactPaymentPanel = reactular(PaymentPanel, [], 'reactWrapper')
